import { LogOut, Key, Mail } from "lucide-react";
import { API_BASE } from "../helpers.js";
import { useDemoMode } from "@/hooks/useDemoMode";
import { clearImpersonation, getPortalToken, isImpersonating } from "@/lib/portalSession";

export default function PortalHeader({ branding, client, contactEmail, onChangePassword, primaryColor }) {
  // Iter112 — Badge "Mode Demo" visible uniquement si le portail tourne en mode demo
  const { isDemoMode } = useDemoMode(client);

  const logout = async () => {
    if (isImpersonating()) {
      // Batch #14.338 — « Déconnexion » pendant une navigation « en tant que » = fin de session journalisée, retour admin
      const t = getPortalToken();
      try { await fetch(`${API_BASE}/portal/impersonate/end`, { method: "POST", headers: { Authorization: `Bearer ${t}` } }); } catch { /* best effort */ }
      clearImpersonation();
      window.close();
      setTimeout(() => { if (!window.closed) window.location.replace("/admin/portail-clients"); }, 150);
      return;
    }
    localStorage.removeItem("portalToken");
    localStorage.removeItem("portalUser");
    localStorage.removeItem("mustChangePassword");
    window.location.replace("/portal");
  };

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-30" data-testid="portal-header">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {branding?.logo_url ? (
            <img
              src={`${API_BASE}${branding.logo_url}`}
              alt="Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-full flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <img src="/dg-logo.png" alt="DG Fiscal Flow" className="w-8 h-8 sm:w-10 sm:h-10 object-contain flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                {isDemoMode ? "DG Fiscal Flow — Portail Démo" : (branding?.company_name || "DG Fiscal Flow")}
              </h1>
              {isDemoMode && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-100 to-indigo-100 text-indigo-700 border border-indigo-200 flex-shrink-0"
                  title="Mode demo actif — aucune donnee reelle"
                  data-testid="portal-demo-badge"
                >
                  <span aria-hidden>🎭</span> Mode Demo
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{client.first_name} {client.last_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
          {contactEmail && (
            <a href={`mailto:${contactEmail}`}
              className="flex items-center justify-center gap-1 text-xs border p-2.5 sm:px-2 sm:py-1.5 rounded-lg transition tap-target"
              style={{ color: primaryColor, borderColor: `${primaryColor}40` }}
              aria-label="Contacter votre équipe"
              data-testid="contact-accountant-btn">
              <Mail className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">Contacter</span>
            </a>
          )}
          <button onClick={onChangePassword}
            className="p-2.5 sm:p-1.5 text-gray-600 hover:text-blue-700 border rounded-lg transition flex items-center justify-center tap-target"
            data-testid="change-password-btn"
            aria-label="Changer mon mot de passe"
            title="Changer mon mot de passe">
            <Key className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
          </button>
          <button onClick={logout}
            className="p-2.5 sm:p-1.5 text-red-600 hover:text-red-700 flex items-center justify-center gap-1 tap-target"
            aria-label="Déconnexion"
            data-testid="portal-logout-btn">
            <LogOut className="w-4 h-4 sm:w-3.5 sm:h-3.5" /><span className="hidden sm:inline text-xs">Deconnexion</span>
          </button>
        </div>
      </div>
    </header>
  );
}
